import { ReactNode, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Button, Input } from "@cofano/cck";

import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Radio,
	Tooltip,
	Typography,
	Grid2 as Grid,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { ProductPickerDialog } from "components/products/ProductPicker";

import { DataFormat, Language } from "utils";

import { Product, ProductReviewDialogForm, ProductTypes } from "view/products/utils/product.types";
import { headerMap } from "view/products/utils/ProductUtils";

import { ProductTankerType, productTankerTypeField } from "./fields/ProductTankerType";
import { ProductCargoTankDesignSelect, productCargoTankDesignField, CargoTankDesign } from "./fields/ProductCargoTankDesign";
import { ProductCargoTankTypeSelect, productCargoTankTypeField, CargoTankType } from "./fields/ProductCargoTankType";
import { ProductCargoTankEquipmentSelect, productCargoTankEquipmentField } from "./fields/ProductCargoTankEquipment";
import { ProductSamplingTypeSelect, productSamplingTypeField, SamplingType } from "./fields/ProductSamplingType";
import { ProductTemperatureClass, productTemperatureClassField } from "./fields/ProductTemperatureClass";
import { ProductEquipment, productEquipmentField } from "./fields/ProductEquipment";
import { ExplosionGroup } from "./fields/ProductExplosionGroup";
import { PackingGroup } from "./fields/ProductPackingGroup";

import { GetAdnGoodForProductDocument, GetDangerousGoodsMasterListDocument } from "__generated__/view/products/graphql/product.queries.generated";
import { UpdateProductAdnGoodDocument } from "__generated__/view/products/graphql/product.mutations.generated";
import { AdnGoodWithAdnFragment } from "__generated__/view/products/graphql/product.fragments.generated";

interface ProductReviewDialogProps {
	product: Pick<
		Product,
		| "id"
		| "adnAttributes"
		| "adnGood"
		| "isValidated"
		| "properShippingNameEN"
		| "properShippingNameNL"
		| "properShippingNameDE"
		| "properShippingNameFR"
		| "density"
		| "densityTemperature"
	>;
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

export default function ProductReviewDialog({
	product,
	open,
	onClose,
	refetch,
}: ProductReviewDialogProps) {
	const { adnGood, adnAttributes } = product;

	const PRODUCT_REVIEW_FORM: ProductReviewDialogForm = useMemo(
		() => ({
			adnCode: adnAttributes?.adnCode || null,
			publicationDate: adnAttributes?.publicationDate || "",
			unNumber: adnAttributes?.unNumber || null,
			properShippingNameNL: product.properShippingNameNL || "",
			properShippingNameEN: product.properShippingNameEN || "",
			properShippingNameDE: product.properShippingNameDE || "",
			properShippingNameFR: product.properShippingNameFR || "",
			hazardCode: adnAttributes?.hazardCode || "",
			classification: adnAttributes?.classification || "",
			classNumber: adnAttributes?.classNumber || "",
			packingGroup: adnAttributes?.packingGroup || "",
			tankerType: adnAttributes?.tankerType || "",
			cargoTankDesign: adnAttributes?.cargoTankDesign || "",
			cargoTankType: adnAttributes?.cargoTankType || "",
			cargoTankEquipment: adnAttributes?.cargoTankEquipment?.split(",") || [],
			openingPressureRelieveValve:
				adnAttributes?.openingPressureRelieveValve || "",
			maxFillingDegreePercentage:
				adnAttributes?.maxFillingDegreePercentage || "",
			samplingType: adnAttributes?.samplingType || "",
			permissiblePumpChamberUnderDeck:
				adnAttributes?.permissiblePumpChamberUnderDeck || false,
			temperatureClass: adnAttributes?.temperatureClass || "",
			explosionGroup: adnAttributes?.explosionGroup || "",
			explosionProtectionRequired:
				adnAttributes?.explosionProtectionRequired || false,
			requiredEquipment: adnAttributes?.requiredEquipment?.split(",") || [],
			numberOfBlueConesOrLights:
				adnAttributes?.numberOfBlueConesOrLights || null,
			extraRequirements: adnAttributes?.extraRequirements || "",
			density: product.density || "",
			densityTemperature: product.densityTemperature || null,
		}),
		[ product ],
	);

	// We don't need validations here because there is no manual input
	const { control, setValue, handleSubmit, reset } = useForm({
		defaultValues: PRODUCT_REVIEW_FORM,
	});

	const [ otherAdnGoodDialogOpen, setOtherAdnGoodDialogOpen ] = useState(false);

	const { data: { adnGoodForProduct: newAdnGood } = {}, refetch: refetchNewAdnGood } = useQuery(GetAdnGoodForProductDocument, {
		skip: !adnGood || !open,
		variables: {
			id: product.id,
		},
	});

	const { data: { adnProductList } = {}, loading: adnProductListLoading } = useQuery(GetDangerousGoodsMasterListDocument, {
		skip: !open || !otherAdnGoodDialogOpen,
	});

	const [ otherAdnGood, setOtherAdnGood ] = useState<AdnGoodWithAdnFragment | null>(null);
	const [ selectedAdnGoodId, setSelectedAdnGoodId ] = useState<number | null>(adnGood?.id || null);

	const [ updateProductAdnGood, { loading: updateProductAdnGoodLoading } ] = useMutation(UpdateProductAdnGoodDocument);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const rows = [
		{
			name: "adnCode",
			label: Language("PRODUCT_ADN_ADN_CODE"),
			product: (
				<Controller
					render={({ field: { value } }) => (
						<Typography fontSize={"small"}>{value || "-"}</Typography>
					)}
					name="adnCode"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() => setValue("adnCode", adnGood?.adnCode || null)}
				>
					{adnGood?.adnCode || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() => setValue("adnCode", newAdnGood?.adnCode || null)}
				>
					{newAdnGood?.adnCode || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() => setValue("adnCode", otherAdnGood?.adnCode || null)}
				>
					{otherAdnGood?.adnCode || "-"}
				</CopyableRow>
			),
		},
		{
			name: "publicationDate",
			label: Language("PRODUCT_ADN_PUBLICATION_DATE"),
			product: (
				<Controller
					render={({ field: { value } }) => (
						<Typography fontSize={"small"}>{value || "-"}</Typography>
					)}
					name="publicationDate"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue("publicationDate", adnGood?.adn?.publicationDate || "")
					}
				>
					{adnGood?.adn?.publicationDate || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue("publicationDate", newAdnGood?.adn?.publicationDate || "")
					}
				>
					{newAdnGood?.adn?.publicationDate || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"publicationDate",
							otherAdnGood?.adn?.publicationDate || "",
						)
					}
				>
					{otherAdnGood?.adn?.publicationDate || "-"}
				</CopyableRow>
			),
		},
		{
			name: "unNumber",
			label: headerMap["unNumber"],
			product: (
				<Controller
					render={({ field: { value } }) => (
						<Typography fontSize={"small"}>{value || "-"}</Typography>
					)}
					name="unNumber"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() => setValue("unNumber", adnGood?.unNumber || null)}
				>
					{adnGood?.unNumber || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() => setValue("unNumber", newAdnGood?.unNumber || null)}
				>
					{newAdnGood?.unNumber || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() => setValue("unNumber", otherAdnGood?.unNumber || null)}
				>
					{otherAdnGood?.unNumber || "-"}
				</CopyableRow>
			),
		},
		{
			name: "properShippingNameEN",
			label: headerMap["properShippingNameEN"],
			product: (
				<Controller
					render={({ field }) => <Input {...field} />}
					name="properShippingNameEN"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameEN",
							adnGood?.properShippingNameEN || "",
						)
					}
				>
					{adnGood?.properShippingNameEN || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameEN",
							newAdnGood?.properShippingNameEN || "",
						)
					}
				>
					{newAdnGood?.properShippingNameEN || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameEN",
							otherAdnGood?.properShippingNameEN || "",
						)
					}
				>
					{otherAdnGood?.properShippingNameEN || "-"}
				</CopyableRow>
			),
		},
		{
			name: "properShippingNameNL",
			label: headerMap["properShippingNameNL"],
			product: (
				<Controller
					render={({ field }) => <Input {...field} />}
					name="properShippingNameNL"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameNL",
							adnGood?.properShippingNameNL || "",
						)
					}
				>
					{adnGood?.properShippingNameNL || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameNL",
							newAdnGood?.properShippingNameNL || "",
						)
					}
				>
					{newAdnGood?.properShippingNameNL || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameNL",
							otherAdnGood?.properShippingNameNL || "",
						)
					}
				>
					{otherAdnGood?.properShippingNameNL || "-"}
				</CopyableRow>
			),
		},
		{
			name: "properShippingNameDE",
			label: headerMap["properShippingNameDE"],
			product: (
				<Controller
					render={({ field }) => <Input {...field} />}
					name="properShippingNameDE"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameDE",
							adnGood?.properShippingNameDE || "",
						)
					}
				>
					{adnGood?.properShippingNameDE || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameDE",
							newAdnGood?.properShippingNameDE || "",
						)
					}
				>
					{newAdnGood?.properShippingNameDE || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameDE",
							otherAdnGood?.properShippingNameDE || "",
						)
					}
				>
					{otherAdnGood?.properShippingNameDE || "-"}
				</CopyableRow>
			),
		},
		{
			name: "properShippingNameFR",
			label: headerMap["properShippingNameFR"],
			product: (
				<Controller
					render={({ field }) => <Input {...field} />}
					name="properShippingNameFR"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameFR",
							adnGood?.properShippingNameFR || "",
						)
					}
				>
					{adnGood?.properShippingNameFR || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameFR",
							newAdnGood?.properShippingNameFR || "",
						)
					}
				>
					{newAdnGood?.properShippingNameFR || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"properShippingNameFR",
							otherAdnGood?.properShippingNameFR || "",
						)
					}
				>
					{otherAdnGood?.properShippingNameFR || "-"}
				</CopyableRow>
			),
		},
		{
			name: "classNumber",
			label: headerMap["classNumber"],
			product: (
				<Controller
					render={({ field: { value } }) => (
						<Typography fontSize={"small"}>{value || "-"}</Typography>
					)}
					name="classNumber"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() => setValue("classNumber", adnGood?.classNumber || "")}
				>
					{adnGood?.classNumber || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() => setValue("classNumber", newAdnGood?.classNumber || "")}
				>
					{newAdnGood?.classNumber || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue("classNumber", otherAdnGood?.classNumber || "")
					}
				>
					{otherAdnGood?.classNumber || "-"}
				</CopyableRow>
			),
		},
		{
			name: "classificationCode",
			label: headerMap["classificationCode"],
			product: (
				<Controller
					render={({ field: { value } }) => (
						<Typography fontSize={"small"}>{value || "-"}</Typography>
					)}
					name="classification"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue("classification", adnGood?.classificationCode || "")
					}
				>
					{adnGood?.classificationCode || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue("classification", newAdnGood?.classificationCode || "")
					}
				>
					{newAdnGood?.classificationCode || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue("classification", otherAdnGood?.classificationCode || "")
					}
				>
					{otherAdnGood?.classificationCode || "-"}
				</CopyableRow>
			),
		},
		{
			name: "packingGroup",
			label: headerMap["packingGroup"],
			product: (
				<Controller
					render={({ field: { value } }) => (
						<Typography fontSize={"small"}>{value || "-"}</Typography>
					)}
					name="packingGroup"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() => setValue("packingGroup", adnGood?.packingGroup || "")}
				>
					{codeWithDescription(
						adnGood?.packingGroup,
						PackingGroup as unknown as Record<string, string>,
					)}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue("packingGroup", newAdnGood?.packingGroup || "")
					}
				>
					{codeWithDescription(
						newAdnGood?.packingGroup,
						PackingGroup as unknown as Record<string, string>,
					)}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue("packingGroup", otherAdnGood?.packingGroup || "")
					}
				>
					{codeWithDescription(
						otherAdnGood?.packingGroup,
						PackingGroup as unknown as Record<string, string>,
					)}
				</CopyableRow>
			),
		},
		{
			name: "hazards",
			label: headerMap["hazards"],
			product: (
				<Controller
					render={({ field }) => <Input {...field} />}
					name="hazardCode"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() => setValue("hazardCode", adnGood?.hazards || "")}
				>
					{adnGood?.hazards || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() => setValue("hazardCode", newAdnGood?.hazards || "")}
				>
					{newAdnGood?.hazards || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() => setValue("hazardCode", otherAdnGood?.hazards || "")}
				>
					{otherAdnGood?.hazards || "-"}
				</CopyableRow>
			),
		},
		{
			name: "tankerType",
			label: headerMap["tankerType"],
			product: (
				<Controller
					render={({ field }) => <ProductTankerType {...field} />}
					name="tankerType"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() => setValue("tankerType", adnGood?.tankerType || "")}
				>
					{productTankerTypeField(adnGood?.tankerType)}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() => setValue("tankerType", newAdnGood?.tankerType || "")}
				>
					{productTankerTypeField(newAdnGood?.tankerType)}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() => setValue("tankerType", otherAdnGood?.tankerType || "")}
				>
					{productTankerTypeField(otherAdnGood?.tankerType)}
				</CopyableRow>
			),
		},
		{
			name: "cargoTankDesign",
			label: headerMap["cargoTankDesign"],
			product: (
				<Controller
					render={({ field }) => <ProductCargoTankDesignSelect {...field} />}
					name="cargoTankDesign"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue("cargoTankDesign", adnGood?.cargoTankDesign || "")
					}
				>
					{productCargoTankDesignField(
						adnGood?.cargoTankDesign as
							| keyof typeof CargoTankDesign
							| undefined,
					)}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue("cargoTankDesign", newAdnGood?.cargoTankDesign || "")
					}
				>
					{productCargoTankDesignField(
						newAdnGood?.cargoTankDesign as
							| keyof typeof CargoTankDesign
							| undefined,
					)}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue("cargoTankDesign", otherAdnGood?.cargoTankDesign || "")
					}
				>
					{productCargoTankDesignField(
						otherAdnGood?.cargoTankDesign as
							| keyof typeof CargoTankDesign
							| undefined,
					)}
				</CopyableRow>
			),
		},
		{
			name: "cargoTankType",
			label: headerMap["cargoTankType"],
			product: (
				<Controller
					render={({ field }) => <ProductCargoTankTypeSelect {...field} />}
					name="cargoTankType"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() => setValue("cargoTankType", adnGood?.cargoTankType || "")}
				>
					{productCargoTankTypeField(
						adnGood?.cargoTankType as keyof typeof CargoTankType | undefined,
					)}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue("cargoTankType", newAdnGood?.cargoTankType || "")
					}
				>
					{productCargoTankTypeField(
						newAdnGood?.cargoTankType as keyof typeof CargoTankType | undefined,
					)}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue("cargoTankType", otherAdnGood?.cargoTankType || "")
					}
				>
					{productCargoTankTypeField(
						otherAdnGood?.cargoTankType as
							| keyof typeof CargoTankType
							| undefined,
					)}
				</CopyableRow>
			),
		},
		{
			name: "cargoTankEquipment",
			label: headerMap["cargoTankEquipment"],
			product: (
				<Controller
					render={({ field }) => (
						<ProductCargoTankEquipmentSelect {...field} multiple />
					)}
					name="cargoTankEquipment"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue(
							"cargoTankEquipment",
							adnGood?.cargoTankEquipment?.split(",") || [],
						)
					}
				>
					{productCargoTankEquipmentField(adnGood?.cargoTankEquipment)}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"cargoTankEquipment",
							newAdnGood?.cargoTankEquipment?.split(",") || [],
						)
					}
				>
					{productCargoTankEquipmentField(newAdnGood?.cargoTankEquipment)}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"cargoTankEquipment",
							otherAdnGood?.cargoTankEquipment?.split(",") || [],
						)
					}
				>
					{productCargoTankEquipmentField(otherAdnGood?.cargoTankEquipment)}
				</CopyableRow>
			),
		},
		{
			name: "openingPressureRelieveValve",
			label: headerMap["openingPressureRelieveValve"],
			product: (
				<Controller
					render={({ field }) => <Input {...field} addonAfter="kPa" />}
					name="openingPressureRelieveValve"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue(
							"openingPressureRelieveValve",
							adnGood?.openingPressureRelieveValve || "",
						)
					}
				>
					{adnGood?.openingPressureRelieveValve || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"openingPressureRelieveValve",
							newAdnGood?.openingPressureRelieveValve || "",
						)
					}
				>
					{newAdnGood?.openingPressureRelieveValve || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"openingPressureRelieveValve",
							otherAdnGood?.openingPressureRelieveValve || "",
						)
					}
				>
					{otherAdnGood?.openingPressureRelieveValve || "-"}
				</CopyableRow>
			),
		},
		{
			name: "maxFillingDegreePercentage",
			label: headerMap["maxFillingDegreePercentage"],
			product: (
				<Controller
					render={({ field }) => <Input {...field} addonAfter="%" />}
					name="maxFillingDegreePercentage"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue(
							"maxFillingDegreePercentage",
							adnGood?.maxFillingDegreePercentage || "",
						)
					}
				>
					{adnGood?.maxFillingDegreePercentage || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"maxFillingDegreePercentage",
							newAdnGood?.maxFillingDegreePercentage || "",
						)
					}
				>
					{newAdnGood?.maxFillingDegreePercentage || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"maxFillingDegreePercentage",
							otherAdnGood?.maxFillingDegreePercentage || "",
						)
					}
				>
					{otherAdnGood?.maxFillingDegreePercentage || "-"}
				</CopyableRow>
			),
		},
		{
			name: "density",
			label: headerMap["density"],
			product: (
				<Controller
					render={({ field }) => <Input {...field} type="number" />}
					name="density"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() => setValue("density", adnGood?.density || "")}
				>
					{adnGood?.density || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() => setValue("density", newAdnGood?.density || "")}
				>
					{newAdnGood?.density || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() => setValue("density", otherAdnGood?.density || "")}
				>
					{otherAdnGood?.density || "-"}
				</CopyableRow>
			),
		},
		{
			name: "densityTemperature",
			label: headerMap["densityTemperature"],
			product: (
				<Controller
					render={({ field }) => (
						<Input {...field} value={field.value || ""} addonAfter={"°C"} />
					)}
					name="densityTemperature"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue("densityTemperature", adnGood?.densityTemperature || null)
					}
				>
					{adnGood?.densityTemperature || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"densityTemperature",
							newAdnGood?.densityTemperature || null,
						)
					}
				>
					{newAdnGood?.densityTemperature || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"densityTemperature",
							otherAdnGood?.densityTemperature || null,
						)
					}
				>
					{otherAdnGood?.densityTemperature || "-"}
				</CopyableRow>
			),
		},
		{
			name: "samplingType",
			label: headerMap["samplingType"],
			product: (
				<Controller
					render={({ field }) => <ProductSamplingTypeSelect {...field} />}
					name="samplingType"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() => setValue("samplingType", adnGood?.samplingType || "")}
				>
					{productSamplingTypeField(
						adnGood?.samplingType as keyof typeof SamplingType | undefined,
					)}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue("samplingType", newAdnGood?.samplingType || "")
					}
				>
					{productSamplingTypeField(
						newAdnGood?.samplingType as keyof typeof SamplingType | undefined,
					)}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue("samplingType", otherAdnGood?.samplingType || "")
					}
				>
					{productSamplingTypeField(
						otherAdnGood?.samplingType as keyof typeof SamplingType | undefined,
					)}
				</CopyableRow>
			),
		},
		{
			name: "permissiblePumpChamberUnderDeck",
			label: headerMap["permissiblePumpChamberUnderDeck"],
			product: (
				<Controller
					render={({ field: { value } }) => (
						<Typography fontSize={"small"}>
							{DataFormat.booleanAsText(value) || "-"}
						</Typography>
					)}
					name="permissiblePumpChamberUnderDeck"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue(
							"permissiblePumpChamberUnderDeck",
							adnGood?.permissiblePumpChamberUnderDeck || false,
						)
					}
				>
					{DataFormat.booleanAsText(adnGood?.permissiblePumpChamberUnderDeck) ||
						"-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"permissiblePumpChamberUnderDeck",
							newAdnGood?.permissiblePumpChamberUnderDeck || false,
						)
					}
				>
					{DataFormat.booleanAsText(
						newAdnGood?.permissiblePumpChamberUnderDeck,
					) || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"permissiblePumpChamberUnderDeck",
							otherAdnGood?.permissiblePumpChamberUnderDeck || false,
						)
					}
				>
					{DataFormat.booleanAsText(
						otherAdnGood?.permissiblePumpChamberUnderDeck,
					) || "-"}
				</CopyableRow>
			),
		},
		{
			name: "temperatureClass",
			label: headerMap["temperatureClass"],
			product: (
				<Controller
					render={({ field }) => <ProductTemperatureClass {...field} />}
					name="temperatureClass"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue("temperatureClass", adnGood?.temperatureClass || "")
					}
				>
					{productTemperatureClassField(adnGood?.temperatureClass)}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue("temperatureClass", newAdnGood?.temperatureClass || "")
					}
				>
					{productTemperatureClassField(newAdnGood?.temperatureClass)}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue("temperatureClass", otherAdnGood?.temperatureClass || "")
					}
				>
					{productTemperatureClassField(otherAdnGood?.temperatureClass)}
				</CopyableRow>
			),
		},
		{
			name: "explosionGroup",
			label: headerMap["explosionGroup"],
			product: (
				<Controller
					render={({ field: { value } }) => (
						<Typography fontSize={"small"}>{value || "-"}</Typography>
					)}
					name="explosionGroup"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue("explosionGroup", adnGood?.explosionGroup || "")
					}
				>
					{codeWithDescription(adnGood?.explosionGroup, ExplosionGroup)}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue("explosionGroup", newAdnGood?.explosionGroup || "")
					}
				>
					{codeWithDescription(newAdnGood?.explosionGroup, ExplosionGroup)}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue("explosionGroup", otherAdnGood?.explosionGroup || "")
					}
				>
					{codeWithDescription(otherAdnGood?.explosionGroup, ExplosionGroup)}
				</CopyableRow>
			),
		},
		{
			name: "explosionProtectionRequired",
			label: headerMap["explosionProtectionRequired"],
			product: (
				<Controller
					render={({ field: { value } }) => (
						<Typography fontSize={"small"}>
							{DataFormat.booleanAsText(value) || "-"}
						</Typography>
					)}
					name="explosionProtectionRequired"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue(
							"explosionProtectionRequired",
							adnGood?.explosionProtectionRequired || false,
						)
					}
				>
					{DataFormat.booleanAsText(adnGood?.explosionProtectionRequired) ||
						"-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"explosionProtectionRequired",
							newAdnGood?.explosionProtectionRequired || false,
						)
					}
				>
					{DataFormat.booleanAsText(newAdnGood?.explosionProtectionRequired) ||
						"-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"explosionProtectionRequired",
							otherAdnGood?.explosionProtectionRequired || false,
						)
					}
				>
					{DataFormat.booleanAsText(
						otherAdnGood?.explosionProtectionRequired,
					) || "-"}
				</CopyableRow>
			),
		},
		{
			name: "requiredEquipment",
			label: headerMap["requiredEquipment"],
			product: (
				<Controller
					render={({ field }) => <ProductEquipment {...field} />}
					name="requiredEquipment"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue(
							"requiredEquipment",
							adnGood?.requiredEquipment?.split(",") || [],
						)
					}
				>
					{productEquipmentField(adnGood?.requiredEquipment)}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"requiredEquipment",
							newAdnGood?.requiredEquipment?.split(",") || [],
						)
					}
				>
					{productEquipmentField(newAdnGood?.requiredEquipment)}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"requiredEquipment",
							otherAdnGood?.requiredEquipment?.split(",") || [],
						)
					}
				>
					{productEquipmentField(otherAdnGood?.requiredEquipment)}
				</CopyableRow>
			),
		},
		{
			name: "numberOfBlueConesOrLights",
			label: headerMap["numberOfBlueConesOrLights"],
			product: (
				<Controller
					render={({ field: { value } }) => (
						<Typography fontSize={"small"}>{value || "-"}</Typography>
					)}
					name="numberOfBlueConesOrLights"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue(
							"numberOfBlueConesOrLights",
							adnGood?.numberOfBlueConesOrLights || null,
						)
					}
				>
					{adnGood?.numberOfBlueConesOrLights || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue(
							"numberOfBlueConesOrLights",
							newAdnGood?.numberOfBlueConesOrLights || null,
						)
					}
				>
					{newAdnGood?.numberOfBlueConesOrLights || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue(
							"numberOfBlueConesOrLights",
							otherAdnGood?.numberOfBlueConesOrLights || null,
						)
					}
				>
					{otherAdnGood?.numberOfBlueConesOrLights || "-"}
				</CopyableRow>
			),
		},
		{
			name: "extraRequirements",
			label: headerMap["extraRequirements"],
			product: (
				<Controller
					render={({ field: { value } }) => (
						<Typography fontSize={"small"}>{value || "-"}</Typography>
					)}
					name="extraRequirements"
					control={control}
				/>
			),
			adn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === adnGood?.id}
					onCopy={() =>
						setValue("extraRequirements", adnGood?.extraRequirements || "")
					}
				>
					{adnGood?.extraRequirements || "-"}
				</CopyableRow>
			),
			newVersion: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === newAdnGood?.id}
					onCopy={() =>
						setValue("extraRequirements", newAdnGood?.extraRequirements || "")
					}
				>
					{newAdnGood?.extraRequirements || "-"}
				</CopyableRow>
			),
			otherAdn: (
				<CopyableRow
					copyEnabled={selectedAdnGoodId === otherAdnGood?.id}
					onCopy={() =>
						setValue("extraRequirements", otherAdnGood?.extraRequirements || "")
					}
				>
					{otherAdnGood?.extraRequirements || "-"}
				</CopyableRow>
			),
		},
	];

	const changedRows = useMemo((): string[] => {
		const rows: string[] = [];
		if (!newAdnGood || !adnGood) {
			return rows;
		}
		const keysToCheck = [
			"properShippingNameNL",
			"properShippingNameEN",
			"properShippingNameDE",
			"properShippingNameFR",
			"hazards",
			"classificationCode",
			"classNumber",
			"packingGroup",
			"tankerType",
			"cargoTankDesign",
			"cargoTankType",
			"cargoTankEquipment",
			"openingPressureRelieveValve",
			"maxFillingDegreePercentage",
			"samplingType",
			"permissiblePumpChamberUnderDeck",
			"temperatureClass",
			"explosionGroup",
			"explosionProtectionRequired",
			"requiredEquipment",
			"numberOfBlueConesOrLights",
			"extraRequirements",
			"density",
			"densityTemperature",
		] as const;

		keysToCheck.forEach((key) => {
			if (adnGood[key] !== (newAdnGood)[key]) {
				rows.push(key);
			}
		});

		return rows;
	}, [ newAdnGood, adnGood ]);

	const onCopyAllFields = (adnGood: AdnGoodWithAdnFragment) => {
		setValue("adnCode", adnGood.adnCode || null);
		setValue("publicationDate", adnGood.adn?.publicationDate || "");
		setValue("unNumber", adnGood.unNumber || null);
		setValue("properShippingNameNL", adnGood.properShippingNameNL || "");
		setValue("properShippingNameEN", adnGood.properShippingNameEN || "");
		setValue("properShippingNameDE", adnGood.properShippingNameDE || "");
		setValue("properShippingNameFR", adnGood.properShippingNameFR || "");
		setValue("packingGroup", adnGood.packingGroup || "");
		setValue("classification", adnGood.classificationCode || "");
		setValue("classNumber", adnGood.classNumber || "");
		setValue("density", adnGood.density || "");
		setValue("densityTemperature", Number(adnGood.densityTemperature) || null);
		setValue("tankerType", adnGood.tankerType || "");
		setValue("cargoTankDesign", adnGood.cargoTankDesign || "");
		setValue("cargoTankType", adnGood.cargoTankType || "");
		setValue(
			"cargoTankEquipment",
			adnGood.cargoTankEquipment?.split(",") || [],
		);
		setValue("samplingType", adnGood.samplingType || "");
		setValue("temperatureClass", adnGood.temperatureClass || "");
		setValue(
			"permissiblePumpChamberUnderDeck",
			adnGood.permissiblePumpChamberUnderDeck || false,
		);
		setValue("requiredEquipment", adnGood.requiredEquipment?.split(",") || []);
		setValue("hazardCode", adnGood.hazards || "");
		setValue("explosionGroup", adnGood.explosionGroup || "");
		setValue(
			"explosionProtectionRequired",
			adnGood.explosionProtectionRequired || false,
		);
		setValue(
			"numberOfBlueConesOrLights",
			adnGood.numberOfBlueConesOrLights || null,
		);
		setValue("extraRequirements", adnGood.extraRequirements || "");
		setValue(
			"openingPressureRelieveValve",
			adnGood.openingPressureRelieveValve || "",
		);
		setValue(
			"maxFillingDegreePercentage",
			adnGood.maxFillingDegreePercentage || "",
		);
	};

	const submit = (values: ProductReviewDialogForm) => {
		if (selectedAdnGoodId == null) {
			return;
		}

		updateProductAdnGood({
			variables: {
				id: product.id,
				adnGoodId: selectedAdnGoodId,
				input: {
					tankerType: values.tankerType,
					cargoTankDesign: values.cargoTankDesign,
					cargoTankType: values.cargoTankType,
					cargoTankEquipment: values.cargoTankEquipment,
					samplingType: values.samplingType,
					temperatureClass: values.temperatureClass,
					requiredEquipment: values.requiredEquipment,
					hazardCode: values.hazardCode,
					openingPressureRelieveValve: values.openingPressureRelieveValve,
					maxFillingDegreePercentage: values.maxFillingDegreePercentage,
				},
			},
		}).then(() => {
			setOtherAdnGood(null);
			refetch();
			refetchNewAdnGood();
			onClose();
		});
	};

	useEffect(() => {
		reset(PRODUCT_REVIEW_FORM);
	}, [ PRODUCT_REVIEW_FORM, reset ]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullScreen
			maxWidth={"lg"}>
			<DialogTitle>
				<Typography color={"primary"} variant={"h6"} fontWeight={"bold"}>
					Product Review | ADN {adnGood?.adnCode}
				</Typography>
			</DialogTitle>

			<DialogContent dividers>
				<Box sx={{ display: "flex", justifyContent: "end", marginBottom: 2 }}>
					<Box mr={1}>
						<Tooltip
							title={intl.formatMessage({ id: "PRODUCT_REVIEW_SELECT_GOOD" })}
						>
							<span>
								<Button
									onClick={() => setOtherAdnGoodDialogOpen(true)}
									icon={<AddIcon />}
									variant="outlined"
								/>
							</span>
						</Tooltip>
					</Box>

					<Box>
						<Tooltip
							title={intl.formatMessage({ id: "PRODUCT_REVIEW_REMOVE_GOOD" })}
						>
							<span>
								<Button
									disabled={!otherAdnGood}
									onClick={() => {
										setOtherAdnGood(null);

										if (selectedAdnGoodId === otherAdnGood?.id) {
											setSelectedAdnGoodId(null);
										}
									}}
									color="danger"
									icon={<DeleteIcon />}
									variant="outlined"
								/>
							</span>
						</Tooltip>
					</Box>
				</Box>

				<Grid
					container
					spacing={2}
					sx={{
						mb: 2,
						pb: 1,
						borderBottom: "2px solid #eee",
						position: "sticky",
						top: 0,
						bgcolor: "white",
						zIndex: 1,
					}}
				>
					{!isMobile && (
						<>
							<Grid size={{ md: 2 }}>
								<Typography fontWeight="bold" fontSize="small">
									{Language("PRODUCT_REVIEW_ATTRIBUTES")}
								</Typography>
							</Grid>
							<Grid size={{ md: 4 }}>
								<Typography fontWeight="bold" fontSize="small">
									{Language("PRODUCT_REVIEW_PRODUCT")}
								</Typography>
							</Grid>
						</>
					)}

					<Grid size={{ xs: 12, md: 6 }}>
						<Box
							sx={{
								display: "flex",
								width: "100%",
								alignItems: "center",
								gap: 1,
							}}
						>
							{[ otherAdnGood, adnGood, newAdnGood ].map((good, idx) => {
								if (!good) {
									return null;
								}

								let labelKey = "";
								if (good.id === adnGood?.id) {
									labelKey = "PRODUCT_REVIEW_CURRENT_ADN";
								} else if (good.id === newAdnGood?.id) {
									labelKey = "PRODUCT_REVIEW_NEW_VERSION";
								} else {
									labelKey = "PRODUCT_REVIEW_OTHER_ADN";
								}
								return (
									<ColumnTitle
										key={idx}
										title={Language(labelKey)}
										disabled={selectedAdnGoodId !== good.id}
										selected={selectedAdnGoodId === good.id}
										onSelect={() => setSelectedAdnGoodId(good.id)}
										onCopyAll={() => onCopyAllFields(good)}
									/>
								);
							})}
						</Box>
					</Grid>
				</Grid>

				{rows.map((r, i) => (
					<Grid
						container
						spacing={2}
						key={i}
						sx={{
							py: 2,
							borderBottom: "1px solid #f0f0f0",
							alignItems: "center",
							...(changedRows.includes(r.name) && { bgcolor: "#fff9f2" }),
						}}
					>
						<Grid size={{ xs: 12, md: 2 }}>
							<Typography fontSize="small" fontWeight="bold">
								{r.label}
							</Typography>
						</Grid>

						<Grid size={{ xs: 12, md: 4 }}>
							<Box sx={{ width: "100%" }}>{r.product}</Box>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<Box
								sx={{
									display: "flex",
									flexDirection: isMobile ? "column" : "row",
									gap: 1,
								}}
							>
								{otherAdnGood && (
									<Box
										sx={{
											flex: 1,
											p: 1,
											borderRadius: 1,
											border: "1px dashed #ccc",
										}}
									>
										<Typography fontSize="small">{r.otherAdn}</Typography>
									</Box>
								)}

								<Box
									sx={{
										flex: 1,
										p: 1,
										borderRadius: 1,
										bgcolor: "#f5f5f5",
										pl: isMobile ? 0 : 1,
									}}
								>
									<Typography fontSize="small">{r.adn}</Typography>
								</Box>

								{newAdnGood && (
									<Box
										sx={{
											flex: 1,
											p: 1,
											borderRadius: 1,
											border: "1px solid #4caf50",
											bgcolor: "#f1f8e9",
										}}
									>
										<Typography fontSize="small">{r.newVersion}</Typography>
									</Box>
								)}
							</Box>
						</Grid>
					</Grid>
				))}

				<ProductPickerDialog
					contentLoading={adnProductListLoading}
					open={otherAdnGoodDialogOpen}
					onClose={() => setOtherAdnGoodDialogOpen(false)}
					onChange={(good) => {
						setSelectedAdnGoodId(good?.id || null);
						setOtherAdnGood(good ?? null);
					}}
					type={ProductTypes.ADN}
					list={adnProductList?.goods || []}
				/>
			</DialogContent>

			<DialogActions>
				<Button
					onClick={handleSubmit(submit)}
					loading={updateProductAdnGoodLoading}
				>
					{Language("SAVE")}
				</Button>

				<Button onClick={onClose} variant="outlined">
					{Language("CANCEL")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

const ColumnTitle = ({
	title,
	disabled,
	selected,
	onSelect,
	onCopyAll,
}: {
	title: ReactNode;
	disabled: boolean;
	selected: boolean;
	onSelect: () => void;
	onCopyAll: () => void;
}) => {
	return (
		<>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<Radio
						checked={selected}
						onChange={onSelect}
						size={"small"}
						sx={{ padding: 0 }}
					/>
					<Typography fontWeight={"bold"} ml={1} fontSize={"small"}>
						{title}
					</Typography>
				</Box>

				{!disabled && (
					<Button variant="outlined" onClick={onCopyAll} size="small">
						{Language("PRODUCTS_REVIEW_COPY_ALL")}
					</Button>
				)}
			</Box>
		</>
	);
};

const codeWithDescription = (
	code: string | null | undefined,
	map: Record<string, string>,
): string => {
	if (!code) {
		return "-";
	}
	const desc = map[code];
	return desc ? `${code} – ${desc}` : code;
};

const CopyableRow = ({
	onCopy,
	copyEnabled,
	children,
}: {
	onCopy: () => void;
	copyEnabled: boolean;
	children: ReactNode;
}) => (
	<Box sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
		<Box sx={{ width: "100%" }}>
			<Typography fontSize={"small"}>{children}</Typography>
		</Box>
		{copyEnabled && (
			<Button variant="outlined" size="small" onClick={onCopy}>
				{Language("COPY")}
			</Button>
		)}
	</Box>
);
